<template>
  <div class="form-question-group-container">
    <div class="divider-global full-width"></div>

    <Typography v-if="PrimaryQuestion.enableBulkUpload && $auth.isAuthenticated" variant="body-2"
      >Enter below or
      <Button
        text
        @click="onOpenCSVWizard"
        :disabled="disableCSVWizard"
        data-testid="upload-bulk-file-button"
        >Upload Bulk File</Button
      ></Typography
    >
    <div
      v-for="(question, i) in questions"
      :key="`${question.uuid}`"
      class="form-question-group-items"
    >
      <FormQuestionGroupItem
        :question="question"
        :formId="formId"
        :childQuestions="childQuestions"
        :validationRules="validationRules"
        :apiErrors="apiErrors"
        :hideDelete="questions.length === 1"
        :answers="getQuestionAnswersByUuid(question.uuid)"
        @delete-group="onDeleteGroup(question.uuid, i)"
        @update-responses="updateGroupAnswerArray($event, i)"
      ></FormQuestionGroupItem>

      <Button
        v-if="PrimaryQuestion.repeated && i === questions.length - 1"
        class="align-self-start"
        outlined
        @click="onDuplicateGroup(question.formQuestionId)"
        >+ Add</Button
      >

      <div class="divider-global full-width"></div>
    </div>
    <!-- CSV Wizard -->
    <CSVWizard
      :value="displayCSVWizard"
      :columns="CSVColumns"
      @close="displayCSVWizard = false"
      @submitted="onCSVWizardSubmitted"
    />
  </div>
</template>

<script>
// Services

// Components
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";

import FormQuestionGroupItem from "@/components/forms/FormQuestionGroupItem";
import CSVWizard from "@/components/csv-wizard/CSVWizard.vue";

import { v4 as uuidv4 } from "uuid";

export default {
  name: "FormQuestionGroup",
  props: {
    questions: {
      type: Array,
      default: () => [],
    },
    childQuestions: {
      type: Array,
      default: () => [],
    },
    formId: Number,
    validationRules: Array,
    apiErrors: Array,
  },
  components: { FormQuestionGroupItem, CSVWizard, Typography, Button },
  emits: ["duplicate-group", "delete-group", "update-responses"],
  data() {
    return {
      groupAnswers: [],
      flatGroupAnswers: [],
      displayCSVWizard: false,
      disableCSVWizard: false,
      bulkUploadAnswers: [],
    };
  },
  methods: {
    updateGroupAnswerArray(event, index) {
      this.$set(this.groupAnswers, index, event);
    },
    onOpenCSVWizard() {
      this.displayCSVWizard = true;
    },
    onCSVWizardSubmitted(data) {
      this.disableCSVWizard = true;
      // For each row in the CSV, we emit to duplicate the group
      if (Array.isArray(data)) {
        data.forEach((row) => {
          // Generate a UUID, append the answers, and duplicate the group
          const uuid = uuidv4();
          this.bulkUploadAnswers.push({ uuid, answers: row });
          this.onDuplicateGroup(this.PrimaryQuestion.formQuestionId, uuid);
        });
      }

      // Clear the first group if it's empty
      if (
        this.flatGroupAnswers[0] &&
        this.flatGroupAnswers[0].every((a) => !a.textValue) &&
        this.questions[0]
      ) {
        this.onDeleteGroup(this.questions[0].uuid, 0);
      }
    },
    onDuplicateGroup(formQuestionId, uuid = undefined) {
      this.$emit("duplicate-group", formQuestionId, uuid);
    },
    onDeleteGroup(uuid, groupQuestionIndex) {
      this.$emit("delete-group", uuid, groupQuestionIndex);
    },
    getQuestionByFormQuestionId(formQuestionId) {
      return this.questions.find((q) => q.formQuestionId === formQuestionId);
    },
    getQuestionAnswersByUuid(uuid) {
      const csvRow = this.bulkUploadAnswers.find((a) => a.uuid === uuid);
      if (csvRow) {
        return csvRow.answers;
      }

      return undefined;
    },
  },
  computed: {
    PrimaryQuestion() {
      // We are passed an array of questions. The question itself should be the same for each,
      // so we just need to return the first so we know visually what to show based on question settings
      if (!this.questions || !this.questions.length) return null;
      return this.questions[0];
    },
    CSVColumns() {
      return this.childQuestions.map((q) => {
        return {
          label: q.label,
          value: q.formQuestionId,
        };
      });
    },
  },
  mounted() {},

  watch: {
    groupAnswers: {
      handler: function () {
        let arr = [];
        this.groupAnswers.forEach((group) => {
          let innerGroupArr = [];
          if (Array.isArray(group)) {
            group.forEach((innerGroup) => {
              innerGroupArr = innerGroup.flat(Infinity).filter(Boolean);
              arr.push(innerGroupArr);
            });
          }
        });
        this.flatGroupAnswers = arr;

        this.$emit("update-responses", arr);
      },
      deep: true,
    },
  },
};
</script>
<style scoped>
.form-question-group-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-bottom: 1rem;
}
.form-question-group-items {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
}
</style>
